import React, { useState, useRef, useEffect, useCallback } from 'react'
import { ReactComponent as PlayIcon } from 'assets/svgs/play-icon.svg'
import { ReactComponent as PauseIcon } from 'assets/svgs/pause-icon.svg'
import { AppWaveformVisualizer } from 'components/Common/AppWaveformVisualizer/AppWaveformVisualizer.component'

// Only one voice message plays at a time: starting one pauses the previous one
let activeAudio: HTMLAudioElement | null = null

interface VoiceMessageProps {
  audioUrl: string
  duration?: number
  transcript?: string
  isMyMessage?: boolean
  className?: string
  textContent?: React.ReactNode
}

export const VoiceMessage: React.FC<VoiceMessageProps> = ({
  audioUrl,
  duration: providedDuration,
  transcript,
  isMyMessage = false,
  className = '',
  textContent,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(providedDuration || 0)
  const [showTranscript, setShowTranscript] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Keep state in sync if providedDuration changes (e.g., from message custom data)
  useEffect(() => {
    if (typeof providedDuration === 'number' && providedDuration > 0) {
      setAudioDuration(Math.floor(providedDuration))
    }
  }, [providedDuration])

  // Initialize audio element
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!audioUrl) return
    const audio = new Audio(audioUrl)
    audioRef.current = audio

    const handleLoadedMetadata = () => {
      const metaDuration = audio.duration
      const fallbackDuration = typeof providedDuration === 'number' ? providedDuration : 0
      const chosen =
        isFinite(metaDuration) && !isNaN(metaDuration) && metaDuration > 0
          ? metaDuration
          : fallbackDuration
      if (isFinite(chosen) && !isNaN(chosen)) {
        setAudioDuration(Math.floor(chosen))
      } else {
        console.warn('Invalid audio duration:', chosen)
        setAudioDuration(0)
      }
    }

    const handleTimeUpdate = () => {
      setCurrentTime(Math.floor(audio.currentTime))
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    // Also fires when another voice message takes over playback
    const handlePause = () => {
      setIsPlaying(false)
    }

    const handleError = (e: Event) => {
      console.error('Audio playback error:', e)
      setIsPlaying(false)
    }

    const handleCanPlay = () => {
      // Fallback for duration if loadedmetadata didn't work
      if (audioDuration === 0) {
        const meta = audio.duration
        if (isFinite(meta) && !isNaN(meta) && meta > 0) {
          setAudioDuration(Math.floor(meta))
        } else if (typeof providedDuration === 'number' && providedDuration > 0) {
          setAudioDuration(Math.floor(providedDuration))
        }
      }
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('error', handleError)
      audio.pause()
      if (activeAudio === audio) activeAudio = null
    }
  }, [audioUrl, audioDuration, providedDuration])

  // Toggle playback
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const togglePlayback = useCallback(async () => {
    if (!audioRef.current) return

    try {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        if (activeAudio && activeAudio !== audioRef.current) activeAudio.pause()
        activeAudio = audioRef.current
        await audioRef.current.play()
        setIsPlaying(true)
      }
    } catch (error) {
      console.error('Error toggling audio playback:', error)
      setIsPlaying(false)
    }
  }, [isPlaying])

  // Format time duration
  const formatTime = (seconds: number) => {
    // Handle invalid or infinite values
    if (!seconds || !isFinite(seconds) || isNaN(seconds)) {
      return '0:00'
    }

    const minutes = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  // Calculate progress percentage
  const progressRatio = audioDuration > 0 ? Math.min(currentTime / audioDuration, 1) : 0

  if (!audioUrl) {
    console.error('VoiceMessage: No audioUrl provided')
    return <div className="text-red-500 text-sm">Voice message unavailable</div>
  }

  return (
    <div className={`voice-message ${className}`}>
      {/* Voice Message Bubble */}
      <div
        className={`rounded-2xl max-w-sm ${
          isMyMessage ? 'bg-app-primary-60 text-white' : 'bg-app-white-95 text-app-black-100'
        }`}
      >
        {/* Text Content - rendered on top if provided */}
        {textContent && (
          <div
            className={`px-3 pt-3 pb-2 ${
              isMyMessage
                ? 'text-white'
                : // Callers pass white text; recolor it like other people's text messages
                  'text-app-black-100 [&_*:not(a)]:text-inherit [&_*:not(a)]:text-[color:inherit] [&_a]:!text-[#5B93FF]'
            }`}
          >
            {textContent}
          </div>
        )}

        {/* Voice Player Controls */}
        <div className="flex items-center gap-3 px-3 pb-3">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlayback}
            className={`flex-shrink-0 ${isMyMessage ? 'text-white' : 'text-app-primary-60'}`}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <PauseIcon
                className={`w-4 h-4 ${isMyMessage ? '' : '[&_*]:fill-app-primary-60'}`}
                fill="white"
              />
            ) : (
              <PlayIcon
                className={`w-4 h-4 ${isMyMessage ? '' : '[&_*]:fill-app-primary-60'}`}
                fill="white"
              />
            )}
          </button>

          {/* Waveform Container */}
          <div className="flex-1 min-w-0">
            <AppWaveformVisualizer
              mode="progress"
              progress={progressRatio}
              activeColorClass={isMyMessage ? 'bg-white' : 'bg-app-primary-60'}
              inactiveColorClass={isMyMessage ? 'bg-white/40' : 'bg-app-grey-40'}
              className="w-full"
            />
          </div>

          {/* Duration */}
          <div
            className={`flex-shrink-0 text-sm font-medium ${
              isMyMessage ? 'text-white/90' : 'text-app-grey-70'
            }`}
          >
            {formatTime(audioDuration)}
          </div>

          {/* Playback Speed Control */}
          <button
            onClick={() => {
              if (audioRef.current) {
                const newRate = playbackRate >= 2 ? 0.5 : playbackRate + 0.25
                audioRef.current.playbackRate = newRate
                setPlaybackRate(newRate)
              }
            }}
            className={`flex-shrink-0 ${
              isMyMessage
                ? 'bg-app-grey-10 hover:bg-app-grey-20'
                : 'bg-app-white-100 hover:bg-app-grey-30'
            } rounded-[8px] px-[12px] py-[6px] text-md font-medium text-app-primary-130 transition-colors`}
          >
            {playbackRate}x
          </button>
        </div>
      </div>

      {/* Transcript Toggle */}
      {transcript && (
        <div className="mt-2">
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            className="text-app-primary-60 text-sm hover:text-app-primary-70 transition-colors"
          >
            {showTranscript ? 'Hide transcript' : 'View transcript'}
          </button>
        </div>
      )}

      {/* Transcript Content */}
      {transcript && showTranscript && (
        <div className="mt-2 p-3 bg-app-grey-10 rounded-lg text-sm text-app-grey-80">
          <p>{transcript}</p>
        </div>
      )}
    </div>
  )
}
